import type {AvailableLicenses, ListPackageResponse} from 'api/productApi'
import type {TypePackageTypeValue} from 'constants/AnalysisConstant'
import type {i18n} from 'i18next'
import useProductManagement from 'features/productManagement/useProductManagement'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'

const zScores = [
  {
    label: '1.96',
    value: 1.96,
  },
  {
    label: '2.58',
    value: 2.58,
  },
  {
    label: '3',
    value: 3,
  },
]

export type AvailablePackageItem = Omit<ListPackageResponse, 'displayInfos'> & {
  displayInfo: string
}

const getAvailablePackageItemsAtLicense = (
  licenses: AvailableLicenses[],
  packageItems: ListPackageResponse[] | null,
  i18n: i18n,
): AvailablePackageItem[] => {
  const pkgItemIdsInLicenses = Array.from(
    new Set(licenses.map((license) => license.packageItemId)),
  )

  const availablePackageItems = packageItems
    ?.filter((pkgItem) => pkgItemIdsInLicenses.includes(`${pkgItem.itemId}`))
    .map((pkgItem) => ({
      ...pkgItem,
      displayInfo:
        pkgItem.displayInfos.find((info) => info.langCode === i18n.language)
          ?.name ?? pkgItem.displayInfos[0].name,
    }))
    .sort((a, b) => a.order - b.order)

  return availablePackageItems ?? []
}

const addPreRequisites = (
  packageItems: AvailablePackageItem[],
  cls: string,
  types: TypePackageTypeValue[],
) => {
  if (types.length === 0) return []

  const prerequisites: AvailablePackageItem[] = []

  types.forEach((type) => {
    const add = packageItems.filter(
      (item) => cls.includes(item.cls) && item.type === type,
    )
    if (add.length === 0) return
    prerequisites.push(...add)
    add.forEach((item) => {
      prerequisites.push(
        ...addPreRequisites(packageItems, item.cls, item.prerequisites),
      )
    })
  })

  return prerequisites
}

const removePreRequisites = (
  packageItems: AvailablePackageItem[],
  cls: string,
  type?: TypePackageTypeValue,
) => {
  if (!type) return []

  const toRemove: AvailablePackageItem[] = []
  const removal = packageItems.filter(
    (item) =>
      item.cls === cls &&
      item.prerequisites.includes(type) &&
      item.target !== 'HealthCare',
  )
  toRemove.push(...removal)
  removal.forEach((item) => {
    toRemove.push(...removePreRequisites(packageItems, item.cls, item.type))
  })

  return Array.from(new Set(toRemove)) // Remove duplicates
}

const rebalanceSelectedPackageItems = (
  packageItems: AvailablePackageItem[],
  prevSelectedItems: AvailablePackageItem[],
  selectedItemId: string,
): AvailablePackageItem[] => {
  const selectedItem = packageItems.find(
    (item) => item.itemId === selectedItemId,
  )
  if (!selectedItem) return prevSelectedItems

  const hasEEG = selectedItem.cls.includes('EEG')
  const hasHRV = selectedItem.cls.includes('HRV')
  let toRemove: AvailablePackageItem[] = []
  if (!hasEEG)
    toRemove = [
      ...removePreRequisites(packageItems, 'EEG', 'Individual'),
      ...packageItems.filter(
        (item) => item.cls === 'EEG' && item.type === 'Individual',
      ),
    ]
  if (!hasHRV)
    toRemove = [
      ...removePreRequisites(packageItems, 'HRV', 'Individual'),
      ...packageItems.filter(
        (item) => item.cls === 'HRV' && item.type === 'Individual',
      ),
    ]

  let newSelected = [...prevSelectedItems]

  // Remove conflicting packages
  newSelected = newSelected.filter(
    (item) => !toRemove.some((rem) => rem.itemId === item.itemId),
  )
  newSelected = newSelected.filter((item) => item.target !== 'HealthCare')

  // Add selected package
  newSelected.push(selectedItem)
  newSelected.push(
    ...addPreRequisites(
      packageItems,
      selectedItem.cls,
      selectedItem.prerequisites,
    ),
  )
  if (hasEEG) {
    const norm = packageItems.find(
      (item) => item.cls === 'EEG' && item.type === 'Norm',
    )
    if (norm) newSelected.push(norm)
  }

  return Array.from(new Set(newSelected)) // Remove duplicates
}

const rebalanceCheckedPackageItems = (
  packageItems: AvailablePackageItem[],
  prevSelectedItems: AvailablePackageItem[],
  checked: boolean,
  selectedItem: AvailablePackageItem,
): AvailablePackageItem[] => {
  let newSelected = [...prevSelectedItems]

  if (checked) {
    newSelected.push(selectedItem)
    newSelected.push(
      ...addPreRequisites(packageItems, selectedItem.cls, [selectedItem.type]),
    )
    // summary 중복 선택 제거
    if (selectedItem.type === 'Summary')
      newSelected = newSelected.filter(
        (item) =>
          !(item.cls === selectedItem.cls && item.type === 'SummaryAdvanced'),
      )
    else if (selectedItem.type === 'SummaryAdvanced')
      newSelected = newSelected.filter(
        (item) => !(item.cls === selectedItem.cls && item.type === 'Summary'),
      )
    else if (selectedItem.type === 'Mci')
      newSelected = newSelected.filter(
        (item) => !(item.cls === selectedItem.cls && item.type === 'Mci8'),
      )
    else if (selectedItem.type === 'Mci8')
      newSelected = newSelected.filter(
        (item) => !(item.cls === selectedItem.cls && item.type === 'Mci'),
      )
  } else {
    newSelected = newSelected.filter(
      (item) => `${item.itemId}` !== `${selectedItem.itemId}`,
    )
    const toRemove = removePreRequisites(
      packageItems,
      selectedItem.cls,
      selectedItem.type,
    )
    newSelected = newSelected.filter(
      (item) => !toRemove.some((rem) => rem.itemId === item.itemId),
    )
  }

  return Array.from(new Set(newSelected)) // Remove duplicates
}

export type HandleChangeSelectedPackageItems = (selectedItemId: string) => void

export type HandleCheckedSelectedPackageItems = (
  checked: boolean,
  selectedItem: AvailablePackageItem,
) => void

export type UseCheckupItemViewModelReturn = {
  availablePackageItems: AvailablePackageItem[]
  selectedPackageItems: AvailablePackageItem[]
  handleCheckedSelectedPackageItems: HandleCheckedSelectedPackageItems
  handleChangeSelectedPackageItems: HandleChangeSelectedPackageItems
  zScores: {label: string; value: number}[]
}

export type OnChangeSelectedPackageItems = (
  selectedItems: AvailablePackageItem[],
) => void

export type UseCheckupItemViewModelProps = {
  initialSelectedPackageItemIds: string[]
  onChangeSelectedPackageItems?: OnChangeSelectedPackageItems
}
export default function useCheckupItemViewModel({
  initialSelectedPackageItemIds,
  onChangeSelectedPackageItems,
}: UseCheckupItemViewModelProps): UseCheckupItemViewModelReturn {
  const {i18n} = useTranslation()
  const {listPackage: packageItems, listAvailableLicenses: availableLicenses} =
    useProductManagement()

  const availablePackageItems = useMemo<AvailablePackageItem[]>(
    () =>
      getAvailablePackageItemsAtLicense(availableLicenses, packageItems, i18n),
    [availableLicenses, packageItems, i18n],
  )

  const [selectedPackageItems, setSelectedPackageItems] = useState<
    AvailablePackageItem[]
  >([])

  const prevInitialIdsRef = useRef<string>('')
  useEffect(() => {
    const currentIds = [...initialSelectedPackageItemIds].sort().join(',')
    if (prevInitialIdsRef.current === currentIds) return
    prevInitialIdsRef.current = currentIds

    const selectedItems = availablePackageItems.filter((item) =>
      initialSelectedPackageItemIds.includes(`${item.itemId}`),
    )
    setSelectedPackageItems(selectedItems)
  }, [availablePackageItems, initialSelectedPackageItemIds])

  useEffect(() => {
    if (onChangeSelectedPackageItems) {
      onChangeSelectedPackageItems(selectedPackageItems)
    }
  }, [selectedPackageItems, onChangeSelectedPackageItems])

  const handleChangeSelectedPackageItems =
    useCallback<HandleChangeSelectedPackageItems>(
      (selectedItemId: string) => {
        console.log('Selected Item ID:', selectedItemId)
        setSelectedPackageItems((prevSelectedItems) =>
          rebalanceSelectedPackageItems(
            availablePackageItems,
            prevSelectedItems,
            selectedItemId,
          ),
        )
      },
      [availablePackageItems],
    )

  const handleCheckedSelectedPackageItems =
    useCallback<HandleCheckedSelectedPackageItems>(
      (checked: boolean, selectedItem: AvailablePackageItem) => {
        console.log('Checked Item:', selectedItem, 'Checked:', checked)
        setSelectedPackageItems((prevSelectedItems) =>
          rebalanceCheckedPackageItems(
            availablePackageItems,
            prevSelectedItems,
            checked,
            selectedItem,
          ),
        )
      },
      [availablePackageItems],
    )

  return {
    availablePackageItems,
    selectedPackageItems,
    handleChangeSelectedPackageItems,
    handleCheckedSelectedPackageItems,
    zScores,
  }
}
